import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminShell from '../../components/admin/AdminShell';
import CustomerTypeBadge from '../../components/admin/CustomerTypeBadge';
import CustomerStatusBadge from '../../components/admin/CustomerStatusBadge';
import CustomerActionsMenu from '../../components/admin/CustomerActionsMenu';
import { useAuth } from '../../App';
import { adminApi } from '../../lib/api';
import { formatCurrency, formatDateTime, getPaymentStatusClass, getCheckInStatusClass, PAYMENT_STATUS_LABELS, CHECK_IN_STATUS_LABELS, CUSTOMER_TYPE_LABELS } from '../../lib/utils';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { ArrowLeft, Phone, Mail, MessageCircle, Calendar, Users, Hash, UserCheck, AlertTriangle } from 'lucide-react';
import { LoadingBlock } from '../../components/common/States';

export default function AdminCustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  
  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingCustomerType, setPendingCustomerType] = useState(null);

  const canEditCustomerType = user?.role === 'super_admin' || user?.role === 'manager';

  useEffect(() => {
    fetchCustomer();
  }, [id]);

  const fetchCustomer = () => {
    setLoading(true);
    adminApi.getCustomer(id)
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load customer'))
      .finally(() => setLoading(false));
  };

  const handleToggleActive = async () => {
    if (!data?.customer) return;
    setToggling(true);
    try {
      const updated = await adminApi.updateCustomer(id, { is_active: !data.customer.is_active });
      setData(d => ({ ...d, customer: updated.data }));
      toast.success(updated.data.is_active ? 'Customer activated' : 'Customer deactivated');
    } catch { toast.error('Failed to update'); }
    finally { setToggling(false); }
  };

  const handleCustomerTypeChange = (newType) => {
    if (!data?.customer || !canEditCustomerType) return;
    if (newType === data.customer.customer_type) return; // No change
    
    // Show confirmation dialog
    setPendingCustomerType(newType);
    setShowConfirmDialog(true);
  };

  const confirmCustomerTypeChange = async () => {
    if (!pendingCustomerType) return;
    
    try {
      const updated = await adminApi.updateCustomer(id, { customer_type: pendingCustomerType });
      setData(d => ({ ...d, customer: updated.data }));
      toast.success(`Customer type changed to ${CUSTOMER_TYPE_LABELS[pendingCustomerType]}`);
    } catch { 
      toast.error('Failed to update customer type'); 
    } finally {
      setShowConfirmDialog(false);
      setPendingCustomerType(null);
    }
  };

  const cancelCustomerTypeChange = () => {
    setShowConfirmDialog(false);
    setPendingCustomerType(null);
  };

  if (loading) return <AdminShell title="Customer"><LoadingBlock rows={6} /></AdminShell>;
  if (!data) return <AdminShell title="Customer"><p className="text-[#B9B9B9]">Not found.</p></AdminShell>;

  const { customer, checkins } = data;

  return (
    <AdminShell
      title="Customer Details"
      actions={
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/customers')} className="gap-1.5 text-[#B9B9B9] text-xs">
          <ArrowLeft size={13} /> Back
        </Button>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Profile card */}
        <div className="surface-card card-highlight rounded-xl p-5">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#D4A843]/15 border border-[#D4A843]/30 flex items-center justify-center">
                <span className="text-[#D4A843] text-lg font-bold">{customer.full_name?.charAt(0)}</span>
              </div>
              <div>
                <h2 className="text-[#F2F2F2] font-['EB_Garamond'] text-xl font-semibold">{customer.full_name}</h2>
                <p className="text-[#B9B9B9] text-sm">{customer.phone}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <CustomerStatusBadge status={customer.status} size="lg"/>
              <CustomerActionsMenu
                customer={customer}
                currentRole={user?.role}
                onChange={fetchCustomer}
              />
            </div>
          </div>

          {/* Customer Type Selector */}
          <div className="mb-4 pb-4 border-b border-[#2A2A2A]">
            <label className="text-[#6F6F6F] text-xs uppercase tracking-wider block mb-2">Customer Type</label>
            {canEditCustomerType ? (
              <Select value={customer.customer_type || 'basic'} onValueChange={handleCustomerTypeChange}>
                <SelectTrigger className="bg-[#111111] border-[#2A2A2A] text-sm w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="old_customer">Old Customer</SelectItem>
                  <SelectItem value="elite">Elite</SelectItem>
                  <SelectItem value="vip">VIP</SelectItem>
                  <SelectItem value="promoter">Promoter</SelectItem>
                  <SelectItem value="blacklisted">Blacklisted</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <div>
                <CustomerTypeBadge customerType={customer.customer_type || 'basic'} />
              </div>
            )}
          </div>

          <div className="space-y-3">
            {[
              { icon: Phone, label: 'Phone', value: customer.phone },
              { icon: Mail, label: 'Email', value: customer.email || '—' },
              { icon: MessageCircle, label: 'WhatsApp', value: customer.whatsapp_number || '—' },
              { icon: Calendar, label: 'Joined', value: formatDateTime(customer.created_at) },
              { icon: UserCheck, label: 'Last Login', value: customer.last_login_at ? formatDateTime(customer.last_login_at) : '—' },
            ].map(row => (
              <div key={row.label} className="flex items-center justify-between text-sm">
                <span className="text-[#6F6F6F] flex items-center gap-1.5"><row.icon size={11} />{row.label}</span>
                <span className="text-[#F2F2F2]">{row.value}</span>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2 border-t border-[#2A2A2A]">
              <span className="text-[#6F6F6F] text-sm">Account active</span>
              <Switch checked={customer.is_active} onCheckedChange={handleToggleActive} disabled={toggling} />
            </div>
          </div>
        </div>

        {/* Booking history */}
        <div className="surface-card card-highlight rounded-xl p-5">
          <h3 className="font-['EB_Garamond'] text-lg text-[#F2F2F2] mb-4">Booking History ({checkins.length})</h3>
          {checkins.length === 0 ? (
            <p className="text-[#6F6F6F] text-sm">No bookings found.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
              {checkins.map(ci => (
                <div
                  key={ci._id}
                  className="p-3 rounded-lg bg-[#111111] border border-[#2A2A2A] cursor-pointer hover:border-[#D4A843]/30 transition-colors"
                  onClick={() => navigate(`/admin/checkins/${ci._id}`)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-mono text-[#D4A843] text-xs">{ci.reference_id}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${getPaymentStatusClass(ci.payment_status)}`}>
                      {PAYMENT_STATUS_LABELS[ci.payment_status] || ci.payment_status}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[#B9B9B9]">
                    <span className="flex items-center gap-1"><Calendar size={10} />{ci.visit_date}</span>
                    <span className="flex items-center gap-1"><Users size={10} />{ci.guests} guests</span>
                    <span className="text-[#D4A843] ml-auto">{formatCurrency(ci.total_amount)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="bg-[#1A1A1A] border-[#2A2A2A]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#F2F2F2] flex items-center gap-2">
              <AlertTriangle className="text-[#D4A843]" size={20} />
              Confirm Customer Type Change
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#B9B9B9]">
              You are about to change the customer type for <span className="text-[#F2F2F2] font-medium">{customer?.full_name}</span>.
              <div className="mt-3 p-3 bg-[#111111] rounded-lg border border-[#2A2A2A]">
                <div className="flex items-center justify-between text-sm">
                  <span>Current Type:</span>
                  <CustomerTypeBadge customerType={customer?.customer_type || 'basic'} />
                </div>
                <div className="flex items-center justify-center my-2">
                  <div className="text-[#6F6F6F]">↓</div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>New Type:</span>
                  <CustomerTypeBadge customerType={pendingCustomerType || 'basic'} />
                </div>
              </div>
              <p className="mt-3 text-xs text-[#6F6F6F]">
                This change will affect how this customer appears in filters, reports, and future marketing campaigns.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={cancelCustomerTypeChange}
              className="bg-[#111111] border-[#2A2A2A] text-[#B9B9B9] hover:bg-[#1A1A1A] hover:text-[#F2F2F2]"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmCustomerTypeChange}
              className="btn-gold"
              data-testid="confirm-customer-type-change"
            >
              Confirm Change
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}
